import { Box } from '@mui/material';
import { CompanyRole } from '@types';
import { useCompanyRelativePath } from 'ogp/hooks';
import { useGetServiceAgreementsForCompany } from 'ogp/hooks/queryHooks/service-agreements/use-get-service-agreements-for-company';
import { AuthenticatedElement } from 'ogp/components/common/authenticated-element';
import {
  ServiceAgreementRole,
  ServiceAgreementsResponse,
} from 'ogp/services/service-agreements.types';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { Header, LwButton, Page, Toolbox } from 'redesign';
import { RenderUnsettledUI } from 'shared/components/render-unsettled-ui/render-unsettled-ui';
import { useFeatureFlagContext } from 'shared/contexts/feature-flag-provider';
import { PolicyGroups } from 'shared/utils/policy-controller';
import { routes } from 'Routes';
import { useGetCollegialLendingScreenTabs } from './hooks/use-get-collegial-lending-screen-tabs';
import { ServiceAgreementList } from './components/service-agreement-list/service-agreement-list';

export const CollegialLendingRedirect = () => {
  const generatePath = useCompanyRelativePath();

  return <Navigate replace to={generatePath(routes.CollegialLendingCustomers)} />;
};

export const CollegialLendingDataProvider = ({ role }: { role: ServiceAgreementRole }) => {
  const serviceAgreementsQuery = useGetServiceAgreementsForCompany(
    role === 'LENDER' ? { isLender: true } : { isBorrower: true }
  );

  if (serviceAgreementsQuery.status !== 'success' || !serviceAgreementsQuery.data) {
    return <RenderUnsettledUI data={serviceAgreementsQuery} />;
  }

  return <CollegialLending data={serviceAgreementsQuery.data} role={role} />;
};

const CollegialLending = ({
  data,
  role,
}: {
  data: ServiceAgreementsResponse;
  role: ServiceAgreementRole;
}) => {
  const { t } = useTranslation('ogp-screen-specific');
  const { allowClientManagement } = useFeatureFlagContext();
  const navigate = useNavigate();
  const generatePath = useCompanyRelativePath();
  const agreements = useGetCollegialLendingScreenTabs(data, role);
  const title =
    role === 'LENDER'
      ? t('collegial-lending.customers.title', {
          defaultValue: 'Service account for Customers',
        })
      : t('collegial-lending.suppliers.title', {
          defaultValue: 'Service account for Suppliers',
        });

  return (
    <Page
      header={<Header titleText={title} />}
      toolbox={
        <Toolbox>
          {role === 'LENDER' && allowClientManagement ? (
            <AuthenticatedElement
              roles={[CompanyRole.ADMIN, CompanyRole.HQADMIN]}
              policyGroup={PolicyGroups.COMPANY}
            >
              <LwButton
                onClick={() => navigate(generatePath(routes.CollegialLendingCustomersCreate))}
              >
                {t('collegial-lending.customers.add-customer', { defaultValue: 'Klant toevoegen' })}
              </LwButton>
            </AuthenticatedElement>
          ) : null}
        </Toolbox>
      }
    >
      <Box width="100%">
        <ServiceAgreementList data={agreements} />
      </Box>
    </Page>
  );
};
